import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CalendarIcon, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import heroPets from "@/assets/hero-pets.webp";

import { serviceFilterOptions, serviceKeyToSlug } from "@/data/serviceLabels";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import { trackWhatsAppClick } from "@/lib/analytics";

const serviceOptions = serviceFilterOptions.map((s) => ({
  value: s.value,
  label: `${s.label} ${s.emoji}`,
}));

const cities = [
"San Salvador",
"Santa Tecla",
"Antiguo Cuscatlán",
"Soyapango",
"Mejicanos"];

const Hero = () => {
  const navigate = useNavigate();
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState<Date>();
  const [serviceOpen, setServiceOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set("city", location);
    if (date) params.set("date", date.toISOString().split("T")[0]);
    const qs = params.toString();
    if (service) {
      const slug = serviceKeyToSlug[service];
      navigate(`/servicios/${slug}${qs ? `?${qs}` : ""}`);
    } else {
      navigate(`/buscar${qs ? `?${qs}` : ""}`);
    }
  };

  return (
    <section className="relative gradient-hero overflow-hidden md:pt-[100px] flex items-start justify-start py-[32px] pb-[40px] pt-[48px] md:py-[60px] md:pb-[100px] md:pt-[60px]">
      {/* Background decorations */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-[#d9eeff]/60 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#3273a4]/15 rounded-full blur-3xl" />

      <div className="container-custom mx-auto px-4 md:px-8 py-8 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <div className="text-left lg:text-left animate-fade-in">
            {/* Mobile: headline + pet side by side */}
            <div className="flex items-center gap-4 md:block">
              <div className="flex-1 min-w-0">
                <h1 className="text-[1.65rem] sm:text-4xl md:text-5xl lg:text-[3.5rem] xl:text-6xl font-fredoka font-bold leading-[1.15] sm:leading-tight mb-3 md:mb-6 md:whitespace-nowrap">
                  <span style={{ color: '#252a2a' }}>Tu mascota merece</span>
                  <br />
                  <span style={{ color: '#252a2a' }}>más que un cuidador.</span>
                  <br />
                  <span className="text-primary">Merece un Compawdre.</span>
                </h1>
              </div>
              <img
                src={heroPets}
                alt="Mascotas felices"
                width={110}
                height={110}
                loading="eager"
                fetchPriority="high"
                className="w-[110px] shrink-0 object-contain md:hidden" />
            </div>

            <p className="text-sm md:text-xl text-muted-foreground mb-4 md:mb-8 max-w-xl lg:mx-0">
              Conectamos a dueños de mascotas con cuidadores verificados en tu zona.
            </p>

            {/* Search Bar Card */}
            <div className="bg-card rounded-[20px] shadow-hover p-3 md:p-6 max-w-[780px] lg:mx-0">
              <div className="flex flex-col md:flex-row gap-1.5 md:gap-3 items-stretch md:items-end">
                {/* Service Dropdown */}
                <div className="flex-1 min-w-0">
                  <label className="font-poppins text-xs font-normal text-muted-foreground mb-1 hidden md:block">Servicio</label>
                  <Popover open={serviceOpen} onOpenChange={setServiceOpen}>
                    <PopoverTrigger asChild>
                      <button className="w-full text-left px-3 py-2 md:py-2.5 rounded-xl border-2 border-soft focus:border-primary outline-none font-poppins text-sm bg-background transition-all duration-200 truncate h-[40px] md:h-[42px]">
                        {service ? serviceOptions.find((s) => s.value === service)?.label : "🐾 Servicio"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-2" align="start">
                      {serviceOptions.map((opt) => <button
                        key={opt.value}
                        onClick={() => {setService(opt.value);setServiceOpen(false);}}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm font-poppins hover:bg-soft transition-colors duration-200",
                          service === opt.value && "bg-primary/10 text-primary font-normal"
                        )}>
                          {opt.label}
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Location Dropdown */}
                <div className="flex-1 min-w-0">
                  <label className="font-poppins text-xs font-normal text-muted-foreground mb-1 hidden md:block">Ubicación</label>
                  <Popover open={locationOpen} onOpenChange={setLocationOpen}>
                    <PopoverTrigger asChild>
                      <button className="w-full text-left px-3 py-2 md:py-2.5 rounded-xl border-2 border-soft focus:border-primary outline-none font-poppins text-sm bg-background transition-all duration-200 flex items-center gap-2 h-[40px] md:h-[42px]">
                        <MapPin size={16} className="text-muted-foreground shrink-0 hidden md:block" />
                        <span className={cn("truncate", location ? "text-foreground" : "text-muted-foreground")}>
                          {location || (<><span className="md:hidden">📍 Ubicación</span><span className="hidden md:inline">Zona o colonia</span></>)}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-2" align="start">
                      {cities.map((city) =>
                      <button
                        key={city}
                        onClick={() => {setLocation(city);setLocationOpen(false);}}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm font-poppins hover:bg-soft transition-colors duration-200",
                          location === city && "bg-primary/10 text-primary font-normal"
                        )}>
                          {city}
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Date Picker */}
                <div className="flex-1 min-w-0">
                  <label className="font-poppins text-xs font-normal text-muted-foreground mb-1 hidden md:block">¿Cuándo?</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="w-full text-left px-3 py-2 md:py-2.5 rounded-xl border-2 border-soft focus:border-primary outline-none font-poppins text-sm bg-background transition-all duration-200 flex items-center gap-2 h-[40px] md:h-[42px]">
                        <CalendarIcon size={16} className="text-muted-foreground shrink-0 hidden md:block" />
                        <span className={cn("truncate", date ? "text-foreground" : "text-muted-foreground")}>
                          {date ? date.toLocaleDateString("es-SV", { day: "2-digit", month: "2-digit", year: "numeric" }) : (<><span className="md:hidden">📅 Fecha</span><span className="hidden md:inline">Fecha</span></>)}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date()}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")} />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* CTA Button */}
                <button
                  onClick={handleSearch}
                  className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors duration-200 font-fredoka font-semibold px-5 py-2.5 rounded-full shadow-soft hover:shadow-hover flex items-center justify-center gap-2 whitespace-nowrap h-[40px] md:h-[42px] shrink-0 w-full md:w-auto mt-0.5 md:mt-0">
                  <Search size={18} />
                  Buscar
                </button>
              </div>
            </div>

            {/* WhatsApp CTA Button - full width below search card */}
            <a
              href={`https://api.whatsapp.com/send?phone=50360651263&text=${encodeURIComponent("Hola, estoy interesado en conocer más sobre Compaws y sus servicios.")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick("hero_button")}
              aria-label="Contáctanos por WhatsApp"
              className="mt-3 md:mt-4 max-w-[780px] w-full flex items-center justify-center gap-2 font-fredoka font-semibold text-white px-5 py-3 md:py-4 rounded-[20px] shadow-soft hover:shadow-hover transition-transform hover:scale-[1.01] text-base md:text-lg"
              style={{ backgroundColor: "#25D366" }}
            >
              <WhatsAppIcon size={22} />
              Contáctanos por WhatsApp
            </a>

            {/* Trust Micro-Badges */}
            <div className="mt-4 md:mt-6 flex flex-wrap gap-x-3 md:gap-x-4 gap-y-1.5 md:gap-y-2 justify-center lg:justify-start">
              <span className="font-poppins text-xs md:text-sm text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                <span className="text-secondary">✓</span> Cuidadores verificados
              </span>
              <span className="font-poppins text-xs md:text-sm text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                <span className="text-accent">⭐</span> Reseñas reales
              </span>
              <span className="font-poppins text-xs md:text-sm text-muted-foreground flex items-center gap-1 whitespace-nowrap">
                <span className="text-primary">🔒</span> Pagos seguros
              </span>
            </div>
          </div>

          {/* Right Content - Pet illustration in white box */}
          <div className="relative justify-center lg:justify-end hidden md:flex">
            <div className="relative">
              <div className="animate-float">
                <img
                  src={heroPets}
                  alt="Mascotas felices"
                  width={480}
                  height={480}
                  loading="eager"
                  fetchPriority="high"
                  className="w-80 md:w-96 lg:w-[480px] object-contain" />
                
              </div>
              {/* Floating fistbump icon */}
              

              
            </div>
          </div>
        </div>
      </div>
      {/* Wave divider into TopCaregivers */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="#eef4e9" />
        </svg>
      </div>
    </section>);
};

export default Hero;